import { Checkbox } from '@timelyapp/react';
import DurationFormat from 'components/DurationFormat';
import NumberFormat from 'components/NumberFormat';
import { TableWidgetProjectBlock } from 'components/superreports/widgets/table/blocks/TableWidgetProjectBlock';
import { Td, Tr } from 'components/ui-blocks/table';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import ForecastTr from 'routes/forecast/components/table/ForecastTr';
import type { ProjectAndAssigneeForecastGroup } from 'routes/forecast/redux/modules/forecast.selectors';
import { batchSelectionActions } from 'routes/forecast/redux/modules/forecast.types';
import type { ThunkDispatch } from 'types/redux';
import { notNull } from 'utils/typesafe';
import { TableIdenticator } from './TableIdenticator';

interface ProjectProps {
  row: ProjectAndAssigneeForecastGroup;
  onClickForecast: (id: number) => void;
  selections: Set<number>;
  canUpdate?: boolean;
  canDelete?: boolean;
  onOpenRow: (id: number) => void;
}

export const ForecastProjectTr = (props: ProjectProps) => {
  const dispatch = useDispatch<ThunkDispatch>();

  const { row, canUpdate, canDelete, onOpenRow } = props;
  const { project, project_summary } = row;
  const [isOpen, setIsOpen] = useState(false);
  const handleToggleOpen = useCallback(() => {
    const newOpenState = !isOpen;
    setIsOpen(newOpenState);
    if (newOpenState === true) {
      onOpenRow(project.id);
    }
  }, [isOpen, onOpenRow, project.id]);

  const rowContext = useMemo(
    () => ({
      type: 'project' as const,
      projectId: project.id,
    }),
    [project.id]
  );

  function renderRows() {
    return row.forecasts.map(forecast => (
      <ForecastTr
        key={forecast.id}
        onClickForecast={props.onClickForecast}
        indent={35}
        forecast={forecast}
        selected={props.selections.has(forecast.id)}
        canUpdate={canUpdate}
        canDelete={canDelete}
        context={rowContext}
        showAssignee
      />
    ));
  }

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const forecastids = useMemo(
    () => row.forecasts.flatMap(f => (f.project?.id === project.id ? f.id : null)).filter(notNull),
    [row, project.id]
  );
  const intersection = forecastids.filter(a => props.selections.has(a));
  const rowChecked = !!intersection.length && intersection.length === forecastids.length;
  const indeterminate = !!intersection.length && intersection.length < forecastids.length;

  const toggleSelection = () => {
    const selections = rowChecked ? [] : forecastids;

    dispatch(
      batchSelectionActions.setSelection({
        selectionKey: 'forecastIds',
        value: selections,
      })
    );
  };

  return (
    <>
      <Tr onClick={handleToggleOpen} selected={isOpen} interactive>
        <Td colSpan={5}>
          <TableIdenticator isExpanded={isOpen} />
          {canUpdate && (
            <Checkbox
              style={{ marginRight: 15 }}
              onClick={stopPropagation}
              onChange={toggleSelection}
              checked={rowChecked}
              indeterminate={indeterminate}
            />
          )}
          <TableWidgetProjectBlock project={project} />
        </Td>
        <Td>
          <div>
            <strong>
              <DurationFormat
                value={project_summary?.total_estimated_minutes}
                format="h[h] m[m]"
                trim="both"
                zeroPlaceholder="0m"
              />
            </strong>{' '}
            est
            <small>
              <NumberFormat value={project_summary?.total_percent} decimals={1} />% logged
            </small>
          </div>
        </Td>
        <Td>
          <div>
            <strong>{project_summary?.task_count}</strong>{' '}
            <small>{project_summary?.task_count === 1 ? 'task' : 'tasks'}</small>
          </div>
        </Td>
      </Tr>
      {isOpen && renderRows()}
    </>
  );
};
export default ForecastProjectTr;
